import { z } from 'zod';
import { deviceStatusSchema } from './device.js';

/**
 * Physical topology. Zones and splitters each nest up to four sublevels below
 * a main node (five tiers total) — enough for zone → area → street and a
 * splitter cascade, while keeping the graph renderable. Main splitters attach
 * to an OLT PON port; sub-splitters chain off their parent.
 */
export const MAX_TOPOLOGY_DEPTH = 4;

export const zoneSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  parentId: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  /** The drawn service area: an open ring of [lat, lng] points. */
  boundary: z.array(z.tuple([z.number(), z.number()])).nullable().optional(),
  /** Ground inside the ring that is not the zone's: cut-outs, and zones it was drawn around (2026-09-15). */
  holes: z.array(z.array(z.tuple([z.number(), z.number()]))).nullable().optional(),
  createdAt: z.number().int(),
  /** Everything placed in the zone: live ONU rows plus ACS-only devices with no ONU row. */
  deviceCount: z.number().int().optional(),
  /** Live chassis ONU rows placed in the zone (2026-09-16) — the "N ONUs" the map's tooltip says. */
  onuCount: z.number().int().optional(),
  splitterCount: z.number().int().optional(),
  /**
   * Only on the answer to a save that carried a boundary, and only when the
   * ring stored is not the ring sent: an overlapping boundary is clipped to
   * its neighbour's edge rather than refused, and the operator is owed a
   * sentence saying what was taken off it. Absent everywhere else.
   */
  boundaryNotice: z.string().nullable().optional(),
  /**
   * Only on the answer to a save in the default "neighbours move" mode: the
   * zones the redraw pushed back or pulled forward, so the page can say who
   * moved and refetch them.
   */
  neighboursMoved: z
    .array(z.object({ id: z.string(), name: z.string(), change: z.string() }))
    .optional(),
});
export type Zone = z.infer<typeof zoneSchema>;

/** How a drawn ring settles against its neighbours (2026-09-15). */
export const boundaryModeSchema = z.enum(['push', 'clip']);
export type BoundaryMode = z.infer<typeof boundaryModeSchema>;

export const upsertZoneSchema = z.object({
  name: z.string().min(1).max(120),
  parentId: z.string().nullable().optional(),
  description: z.string().max(500).nullable().optional(),
  lat: z.coerce.number().min(-90).max(90).nullable().optional(),
  lng: z.coerce.number().min(-180).max(180).nullable().optional(),
  boundary: z.array(z.tuple([z.number(), z.number()])).max(500).nullable().optional(),
  /**
   * `push` (the default): the ring drawn is stored as drawn and the neighbours
   * move — pushed off the ground it covers, pulled onto the ground a redraw
   * released beside them. `clip`: the older rule, the drawn ring yields to
   * its neighbours' edges.
   */
  boundaryMode: boundaryModeSchema.optional(),
});
export type UpsertZoneRequest = z.infer<typeof upsertZoneSchema>;

/** Take a piece out of a zone: as a hole, or as a new zone with exactly that shape. */
export const cutOutRequestSchema = z.object({
  ring: z.array(z.tuple([z.number(), z.number()])).min(3).max(500),
  into: z
    .object({ name: z.string().min(1).max(120), description: z.string().max(500).nullable().optional() })
    .nullable()
    .optional(),
});
export type CutOutRequest = z.infer<typeof cutOutRequestSchema>;
export const cutOutResponseSchema = z.object({
  zone: zoneSchema,
  created: zoneSchema.nullable(),
  notice: z.string().nullable(),
});
export type CutOutResponse = z.infer<typeof cutOutResponseSchema>;

/**
 * Split a zone along a line drawn across it (2026-09-16): the zone keeps one
 * half and a new sibling zone is made of the other. `keepName` renames the
 * half the zone keeps; `newName` is the sibling's.
 */
export const splitRequestSchema = z.object({
  line: z.array(z.tuple([z.number(), z.number()])).min(2).max(200),
  newName: z.string().min(1).max(120),
  keepName: z.string().min(1).max(120).optional(),
});
export type SplitRequest = z.infer<typeof splitRequestSchema>;
export const splitResponseSchema = z.object({
  zone: zoneSchema,
  created: zoneSchema,
  /** Splitters that stood in the new zone's half and were moved to it. */
  splittersMoved: z.number().int(),
  notice: z.string().nullable(),
});
export type SplitResponse = z.infer<typeof splitResponseSchema>;

/**
 * Put splitters (and, through them, ONUs) into the zone whose shape holds
 * their coordinates (2026-09-16). `dryRun` answers what WOULD change and
 * writes nothing. A splitter an operator already placed in a different zone
 * is listed under `disagrees` and left where it is — it was put there on
 * purpose, and a shape is not a better witness than a person.
 */
export const autoAssignRequestSchema = z.object({ dryRun: z.boolean().optional() });
export type AutoAssignRequest = z.infer<typeof autoAssignRequestSchema>;
export const autoAssignResponseSchema = z.object({
  dryRun: z.boolean(),
  splitters: z.object({
    /** Splitters with no zone whose coordinates fall in one — set to it. */
    assigned: z.array(z.object({ id: z.string(), name: z.string(), zone: z.string() })),
    /** Splitters standing in a zone other than the one they are placed in — NOT moved. */
    disagrees: z.array(z.object({ id: z.string(), name: z.string(), zone: z.string(), inZone: z.string() })),
    /** Splitters still in no zone after the run: no coordinates, or standing in no zone's shape. */
    unplaced: z.number().int(),
  }),
  onus: z.object({
    /** ONUs with no zone whose splitter has one (after this run) — set to the splitter's. */
    assigned: z.number().int(),
    /** Live ONUs still in no zone after the run. */
    unplaced: z.number().int(),
  }),
});
export type AutoAssignResponse = z.infer<typeof autoAssignResponseSchema>;

/** `GET /zones/export?format=` — the two files the map can hand out. */
export const zoneExportFormatSchema = z.enum(['geojson', 'kml']);
export type ZoneExportFormat = z.infer<typeof zoneExportFormatSchema>;

/**
 * One polygon of an imported file, judged (2026-09-16). `created` and
 * `updated` name what happened to the row; `skipped` carries `error` saying
 * why nothing did. `notice` is the boundary judge's sentence when the ring
 * stored is not the ring in the file — an import yields to the zones that
 * exist, it never moves them.
 */
export const zoneImportRowSchema = z.object({
  name: z.string(),
  action: z.enum(['created', 'updated', 'skipped']),
  /** The zone's id once it has one; null on a skipped row and on every dry-run create. */
  id: z.string().nullable(),
  notice: z.string().nullable(),
  error: z.string().nullable(),
});
export type ZoneImportRow = z.infer<typeof zoneImportRowSchema>;
export const zoneImportResponseSchema = z.object({
  dryRun: z.boolean(),
  source: z.enum(['geojson', 'kml']).nullable(),
  rows: z.array(zoneImportRowSchema),
  /** Things in the file that were not polygons at all, each a sentence. */
  errors: z.array(z.string()),
});
export type ZoneImportResponse = z.infer<typeof zoneImportResponseSchema>;

/** A splitter is one input fanned out to a fixed set of outputs. */
export const SPLITTER_CAPACITIES = [2, 4, 8, 16, 32] as const;

/**
 * Typical insertion loss of a PLC splitter, in dB, by output count.
 *
 * These are the figures a PON link budget is planned against — the ideal split
 * of a 1:N is 10·log10(N) dB and every real component adds a little on top, so
 * a 1:32 is quoted at 17.1 rather than the theoretical 15.05. They are typical
 * values for planning, not a measurement of the splitter in the ground: an
 * ageing or badly-spliced one loses more, which is exactly the gap worth
 * seeing when a measured ONU reading is compared against the budget.
 */
export const SPLITTER_INSERTION_LOSS_DB: Record<number, number> = {
  2: 3.6,
  4: 7.2,
  8: 10.5,
  16: 13.8,
  32: 17.1,
  64: 20.5,
};

/** The planning figure for a split of this size, interpolated if unlisted. */
export function splitterLossDb(capacity: number): number {
  const known = SPLITTER_INSERTION_LOSS_DB[capacity];
  if (known !== undefined) return known;
  if (!Number.isFinite(capacity) || capacity < 2) return 0;
  // An unlisted size falls back to the ideal split plus the ~1 dB of excess
  // loss the listed figures carry, rather than pretending the loss is zero.
  return Math.round((10 * Math.log10(capacity) + 1) * 10) / 10;
}

/**
 * Where accumulated splitter loss turns amber: past 28 dB there is no ordinary
 * GPON class left to spend — B+ has 28 dB of budget and C+ 32, before any
 * fibre, splices or connectors are accounted for. The signal diagram and the
 * cascade wizard both read THIS constant, so they can never amber at
 * different numbers.
 */
export const SPLITTER_LOSS_AMBER_DB = 28;

/**
 * Attenuation of the fibre itself, dB per kilometre (2026-09-16, operator:
 * fibre routes with measured length feeding the loss readout).
 *
 * 0.35 dB/km is G.652 single-mode at 1310 nm — the UPSTREAM wavelength. The
 * downstream at 1490 nm runs nearer 0.25 dB/km, but a link is planned against
 * the direction with the least to spare, and that is the ONU talking back: a
 * subscriber laser is the cheap, weak end of the link and its wavelength is
 * the lossier one, so the same run costs the upstream a third more than it
 * costs the downstream. Planning against 1310 nm is the conservative figure.
 * Splices and connectors are still on top of this, as they are on top of the
 * splitter table above.
 */
export const FIBRE_DB_PER_KM = 0.35;

/** The fibre's share of the budget for a run of this many metres, rounded to 0.1 dB. Unknown or nothing = 0. */
export function fibreLossDb(lengthM: number | null | undefined): number {
  if (lengthM === null || lengthM === undefined || !Number.isFinite(lengthM) || lengthM <= 0) return 0;
  return Math.round((lengthM / 1000) * FIBRE_DB_PER_KM * 10) / 10;
}

/**
 * Accumulated planning loss of a chain of splits, rounded to 0.1 dB — the one
 * summing rule for every reader of the loss table above.
 */
export function cascadeLossDb(capacities: number[]): number {
  let total = 0;
  for (const capacity of capacities) total += splitterLossDb(capacity);
  return Math.round(total * 10) / 10;
}

/**
 * Capacities of a splitter and every one above it, this one first. Loop-safe:
 * a cyclic parent chain (which the depth guard should make impossible) must
 * not hang a caller.
 */
export function upstreamCapacities(
  id: string,
  all: Array<{ id: string; parentId: string | null; capacity: number }>,
): number[] {
  const byId = new Map(all.map((s) => [s.id, s] as const));
  const capacities: number[] = [];
  const seen = new Set<string>();
  let node = byId.get(id);
  while (node && !seen.has(node.id)) {
    seen.add(node.id);
    capacities.push(node.capacity);
    node = node.parentId ? byId.get(node.parentId) : undefined;
  }
  return capacities;
}

/**
 * Where a splitter physically sits. The light path runs
 * OLT port → ODF port → enclosure splitter → FAT splitter → ONU,
 * so a child may never be an earlier stage than its parent.
 */
export const splitterKindSchema = z.enum(['odf', 'enclosure', 'fat']);
export type SplitterKind = z.infer<typeof splitterKindSchema>;

export const SPLITTER_KIND_LABELS: Record<SplitterKind, string> = {
  odf: 'ODF',
  enclosure: 'Enclosure',
  fat: 'FAT',
};

/** Position of each kind along the light path, for ordering checks. */
export const SPLITTER_KIND_RANK: Record<SplitterKind, number> = {
  odf: 0,
  enclosure: 1,
  fat: 2,
};

/**
 * The model's own refusals, said once. The create route, the cascade builder
 * and the wizard's live plan all quote these strings, so a rule can never be
 * described one way at creation and another way in a plan.
 */
export const SPLITTER_KIND_ORDER_MESSAGE =
  'The light path runs OLT port → ODF → enclosure → FAT → ONU; a child cannot be an earlier stage than its parent';
export const SPLITTER_DEPTH_MESSAGE = `Splitters cascade at most ${MAX_TOPOLOGY_DEPTH} sublevels below a main splitter`;

export const splitterSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  zoneId: z.string().nullable(),
  parentId: z.string().nullable(),
  name: z.string(),
  kind: splitterKindSchema,
  capacity: z.number().int(),
  /** Set on main splitters only: the OLT PON port feeding this cascade. */
  oltId: z.string().nullable(),
  oltPonPort: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  /** Core number/colour of the upstream fiber, enclosure details. */
  notes: z.string().nullable(),
  createdAt: z.number().int(),
  deviceCount: z.number().int().optional(),
  /**
   * Measured fibre from the top of the chain down to this splitter, metres
   * (2026-09-16): the sum of the drawn routes on every hop above it — see
   * `splitterFeedLengths` for the rule. Null when a hop has no route. Set by
   * GET /topology; absent on a create/edit answer.
   */
  feedLengthM: z.number().nullable().optional(),
  /** `fibreLossDb(feedLengthM)` — null exactly when the length is. */
  feedLossDb: z.number().nullable().optional(),
  /**
   * The object-store key of the housing's photograph (2026-09-16, the
   * technician app's "Take a photo" on a splitter), or null. Never a URL:
   * the bytes are read through GET /splitters/:id/photo, and the key changes
   * on every re-take so a client can use it to bust its image cache.
   */
  photoKey: z.string().nullable().optional(),
});
export type Splitter = z.infer<typeof splitterSchema>;

// --- The field map (2026-09-16) ------------------------------------------------

/**
 * The plant as the technician app's Map tab draws it — the answer to
 * `GET /api/topology/field`, readable by whoever works deployment jobs.
 *
 * `GET /topology` is the whole page: every zone with its counts, every
 * splitter with its fill and feed, optical splitters, heat points. A
 * technician holds no `site.read` for it, and the map they need in the field
 * is smaller and different in kind: WHERE the boxes are, the ground each zone
 * covers, and the lines the fibre runs along — so they can find the FAT
 * they are sent to and the platform can say which zone they are standing in.
 * No subscriber travels here: a splitter carries how many ONUs hang off it
 * and which ports are held, never who holds them (the ONUs a person may see
 * on a splitter come from `GET /api/onus?splitterId=`, inside their own read
 * scope).
 */
export const fieldMapZoneSchema = z.object({
  id: z.string(),
  name: z.string(),
  parentId: z.string().nullable(),
  /** The drawn service area — an open ring of [lat, lng] — or null when none is drawn. */
  boundary: z.array(z.tuple([z.number(), z.number()])).nullable(),
  /** Ground inside the ring that is not the zone's; drawn as inner rings. */
  holes: z.array(z.array(z.tuple([z.number(), z.number()]))).nullable(),
});
export type FieldMapZone = z.infer<typeof fieldMapZoneSchema>;

export const fieldMapSplitterSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: splitterKindSchema,
  capacity: z.number().int(),
  zoneId: z.string().nullable(),
  parentId: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  /** Live ONUs placed on it — the fill, as GET /topology counts it. */
  onuCount: z.number().int(),
  /** The output ports ONU rows already hold, ascending — numbers, never names. */
  usedPorts: z.array(z.number().int()),
  photoKey: z.string().nullable(),
});
export type FieldMapSplitter = z.infer<typeof fieldMapSplitterSchema>;

export const fieldMapSchema = z.object({
  zones: z.array(fieldMapZoneSchema),
  splitters: z.array(fieldMapSplitterSchema),
  fibreRoutes: z.array(
    z.object({
      id: z.string(),
      fromKind: z.enum(['olt', 'splitter', 'odf']),
      fromId: z.string(),
      toSplitterId: z.string(),
      path: z.array(z.tuple([z.number(), z.number()])),
      lengthM: z.number(),
    }),
  ),
});
export type FieldMap = z.infer<typeof fieldMapSchema>;

/** What `POST /api/splitters/:id/photo` answers once the bytes are in the store. */
export const splitterPhotoSchema = z.object({
  id: z.string(),
  photoKey: z.string(),
  contentType: z.string(),
  bytes: z.number().int(),
  sha256: z.string(),
  uploadedAt: z.number().int(),
});
export type SplitterPhoto = z.infer<typeof splitterPhotoSchema>;

// --- Fibre routes -------------------------------------------------------------

/**
 * A fibre route is the line the cable actually runs along between two
 * points of the plant (2026-09-16, operator: "fibre routes as lines between
 * splitters with measured length, feeding the loss readout the cascade
 * wizard already has"). It ENDS at a splitter — the housing the fibre feeds
 * — and STARTS at the splitter above it, or at the OLT for the first hop. A
 * splitter has one input, so it has at most one route into it.
 *
 * `fromKind` is `odf` when the start is a splitter row of kind odf, `splitter`
 * for any other housing, `olt` for the chassis. The OLT has no position on
 * the map, so a route from it begins wherever the operator clicked.
 */
export const fibreRouteFromKindSchema = z.enum(['olt', 'splitter', 'odf']);
export type FibreRouteFromKind = z.infer<typeof fibreRouteFromKindSchema>;

export const fibreRouteSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  fromKind: fibreRouteFromKindSchema,
  /** An OLT id when fromKind is `olt`, else a splitter id. */
  fromId: z.string(),
  toSplitterId: z.string(),
  /** The line as laid: [lat, lng] points, first at the start, last at the splitter fed. */
  path: z.array(z.tuple([z.number(), z.number()])),
  /** Metres along `path` — computed server-side from the path on every write, never taken from a client. */
  lengthM: z.number(),
  note: z.string().nullable(),
  createdAt: z.number().int(),
});
export type FibreRoute = z.infer<typeof fibreRouteSchema>;

export const upsertFibreRouteSchema = z.object({
  fromKind: fibreRouteFromKindSchema,
  fromId: z.string().min(1),
  toSplitterId: z.string().min(1),
  path: z.array(z.tuple([z.number().min(-90).max(90), z.number().min(-180).max(180)])).min(2).max(2000),
  note: z.string().max(500).nullable().optional(),
});
export type UpsertFibreRouteRequest = z.infer<typeof upsertFibreRouteSchema>;

/**
 * Metres of measured fibre from the top of the chain down to each splitter,
 * from the routes drawn — the number `feedLengthM` carries on GET /topology,
 * and one rule for it.
 *
 * Walked UPSTREAM along the routes, not along `parentId`: a route says where
 * the cable really goes, and the sum is the routes it passes through. The
 * walk from a splitter follows the route into it, then the route into that
 * route's start, and so on, and ends in one of three ways:
 *
 *  - at an OLT (a route whose start is the chassis): the sum stands;
 *  - at a ROOT splitter with no route into it — the top of the chain, whose
 *    feed is the OLT and is simply not drawn: the sum stands. The OLT has no
 *    position on the map, so that hop is counted when drawn and never
 *    required;
 *  - at a splitter that has a PARENT but no route into it: a hop of real
 *    fibre is unmeasured, so the answer is null — a partial sum would flatter
 *    the budget by however much that hop runs.
 *
 * A splitter with no route into it at all is null. Loop-safe, because the
 * API refuses a route that would close a loop but a reader must not hang on
 * a row that predates the check.
 */
export function splitterFeedLengths(
  splitters: Array<{ id: string; parentId: string | null }>,
  routes: Array<{ fromKind: FibreRouteFromKind; fromId: string; toSplitterId: string; lengthM: number }>,
): Map<string, number | null> {
  const byId = new Map(splitters.map((s) => [s.id, s] as const));
  const into = new Map<string, (typeof routes)[number]>();
  for (const r of routes) if (!into.has(r.toSplitterId)) into.set(r.toSplitterId, r);
  const memo = new Map<string, number | null>();
  const walk = (id: string, seen: Set<string>): number | null => {
    if (memo.has(id)) return memo.get(id)!;
    if (seen.has(id)) return null;
    seen.add(id);
    const route = into.get(id);
    let answer: number | null;
    if (!route) {
      answer = null;
    } else if (route.fromKind === 'olt') {
      answer = route.lengthM;
    } else {
      const above = walk(route.fromId, seen);
      if (above !== null) {
        answer = route.lengthM + above;
      } else {
        // The start has no route into it: fine at the top of the chain, a gap anywhere else.
        const start = byId.get(route.fromId);
        answer = start && start.parentId === null && !into.has(start.id) ? route.lengthM : null;
      }
    }
    if (answer !== null) answer = Math.round(answer * 10) / 10;
    memo.set(id, answer);
    return answer;
  };
  const out = new Map<string, number | null>();
  for (const s of splitters) out.set(s.id, walk(s.id, new Set()));
  return out;
}

export const upsertSplitterSchema = z.object({
  name: z.string().min(1).max(120),
  zoneId: z.string().nullable().optional(),
  parentId: z.string().nullable().optional(),
  kind: splitterKindSchema.default('fat'),
  capacity: z.coerce
    .number()
    .int()
    .refine((v) => (SPLITTER_CAPACITIES as readonly number[]).includes(v), {
      message: `Capacity must be one of ${SPLITTER_CAPACITIES.join(', ')} outputs`,
    })
    .default(8),
  oltId: z.string().nullable().optional(),
  oltPonPort: z.string().max(64).nullable().optional(),
  lat: z.coerce.number().min(-90).max(90).nullable().optional(),
  lng: z.coerce.number().min(-180).max(180).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
});
export type UpsertSplitterRequest = z.infer<typeof upsertSplitterSchema>;

/** An ONU as it appears in the splitter-assignment dialog. */
export const splitterOnuSchema = z.object({
  id: z.string(),
  alias: z.string().nullable(),
  serial: z.string(),
  status: deviceStatusSchema,
  rxPowerDbm: z.number().nullable(),
});
export type SplitterOnu = z.infer<typeof splitterOnuSchema>;

export const splitterOnusSchema = z.object({
  capacity: z.number().int(),
  assigned: z.array(splitterOnuSchema),
  /** Claimed ONUs in the splitter's org that are not on any splitter yet. */
  candidates: z.array(splitterOnuSchema),
  /**
   * Splitter ports already spoken for, read from the ONUs themselves.
   *
   * `assigned` above comes from the TR-069 device rows, which are empty on a
   * fleet managed over OMCI, and carries no port. Anything offering to place an
   * ONU on a port needs to know which ports are taken whether or not the
   * subscriber speaks TR-069.
   */
  takenPorts: z.array(z.number().int()).optional(),
});
export type SplitterOnus = z.infer<typeof splitterOnusSchema>;

/**
 * What a picker needs to place ONE ONU, and nothing else — the answer to
 * `GET /api/onus/:id/placement/options`.
 *
 * The web's Placement card reads the whole topology (`GET /topology`) and
 * then asks each splitter for its taken ports; a phone on a subscriber's
 * wall gets the three lists in one round trip, cut to what the picker shows:
 * zone by name (with its parent, so "Phase 2" can be told from the other
 * "Phase 2"), splitter by name with its size and the ports other ONUs already
 * hold, and where this ONU sits now so the picker opens on it. Picker
 * vocabulary: names and numbers, never row ids in the sentence.
 */
export const placementOptionsSchema = z.object({
  zones: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      parentId: z.string().nullable(),
      /**
       * The zone's drawn ring and holes (2026-09-16), so the phone's
       * Placement card can say which zone the technician is STANDING in and
       * open the picker on it (`zoneAtPosition`). Optional so an older
       * payload still parses; null when the zone has no boundary drawn.
       */
      boundary: z.array(z.tuple([z.number(), z.number()])).nullable().optional(),
      holes: z.array(z.array(z.tuple([z.number(), z.number()]))).nullable().optional(),
    }),
  ),
  splitters: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      zoneId: z.string().nullable(),
      capacity: z.number().int(),
      kind: splitterKindSchema,
      /**
       * Ports OTHER ONUs hold on this splitter, from the ONU rows — the same
       * source `checkPlacement` refuses from, so the picker and the refusal
       * agree. This ONU's own port is deliberately NOT listed: re-stating the
       * port a subscriber's own drop already holds is the same drop, and the
       * write does not count it against them either.
       */
      usedPorts: z.array(z.number().int()),
    }),
  ),
  /** Where the ONU sits now — the ONU's own record, falling back to its linked device's. */
  current: z.object({
    zoneId: z.string().nullable(),
    splitterId: z.string().nullable(),
    splitterPort: z.number().int().nullable(),
  }),
});
export type PlacementOptions = z.infer<typeof placementOptionsSchema>;

export const assignOnusSchema = z.object({
  deviceIds: z.array(z.string().min(1)).min(1).max(128),
});
export type AssignOnusRequest = z.infer<typeof assignOnusSchema>;

/** One payload for the whole topology page: tree plus map points. */
// --- Optical splitters -------------------------------------------------------

/**
 * The physical 1:N device, as distinct from the housing it sits in.
 *
 * The topology tree's odf/enclosure/fat rows are HOUSINGS on the light path;
 * an optical splitter is the actual device inside one of them, and a housing
 * holds any number of them (the operator's rule). Its upstream is field
 * reality the tree cannot select: spliced straight to another splitter, or to
 * a fiber core — a drop cable or an ADSS core — before reaching the parent,
 * with a free-text comment for what only the splicer knows (core colour,
 * tray, loose tube).
 */
export const spliceKindSchema = z.enum(['splitter', 'drop_cable', 'adss']);
export type SpliceKind = z.infer<typeof spliceKindSchema>;

export const SPLICE_KIND_LABELS: Record<SpliceKind, string> = {
  splitter: 'Spliced to another splitter',
  drop_cable: 'Drop cable core',
  adss: 'ADSS core',
};

export const opticalSplitterSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  /** Output count — the N of 1:N. */
  outputs: z.number().int(),
  /** The odf/enclosure/fat row this device sits inside; null = not housed yet. */
  housingId: z.string().nullable(),
  upstreamKind: spliceKindSchema.nullable(),
  /** Set only when upstreamKind is 'splitter': the device it is spliced to. */
  upstreamSplitterId: z.string().nullable(),
  /** What the tree cannot say: core colour, tray, anything from the field. */
  upstreamComment: z.string().nullable(),
  createdAt: z.number().int(),
});
export type OpticalSplitter = z.infer<typeof opticalSplitterSchema>;

export const upsertOpticalSplitterSchema = z.object({
  name: z.string().min(1).max(120),
  outputs: z.coerce
    .number()
    .int()
    .refine((v) => (SPLITTER_CAPACITIES as readonly number[]).includes(v), {
      message: `Outputs must be one of ${SPLITTER_CAPACITIES.join(', ')}`,
    })
    .default(8),
  housingId: z.string().min(1).nullable().optional(),
  upstreamKind: spliceKindSchema.nullable().optional(),
  upstreamSplitterId: z.string().min(1).nullable().optional(),
  upstreamComment: z.string().max(500).nullable().optional(),
});
export type UpsertOpticalSplitterRequest = z.infer<typeof upsertOpticalSplitterSchema>;

export const topologySchema = z.object({
  zones: z.array(zoneSchema),
  splitters: z.array(splitterSchema),
  opticalSplitters: z.array(opticalSplitterSchema).optional(),
  /** Heat points: splitter positions weighted by the ONUs hanging off them. */
  heat: z.array(z.object({ lat: z.number(), lng: z.number(), weight: z.number() })),
  /**
   * What sits in no zone at all (2026-09-16): live ONUs and splitters,
   * org-wide. Always sent by the API; optional here only so a fixture or an
   * older payload still parses.
   */
  unplaced: z.object({ onus: z.number().int(), splitters: z.number().int() }).optional(),
  /**
   * The fibre routes drawn between the plant's points (2026-09-16). Always
   * sent by the API; optional here so the MCP fixture and an older payload
   * still parse.
   */
  fibreRoutes: z.array(fibreRouteSchema).optional(),
});
export type Topology = z.infer<typeof topologySchema>;

// --- Firmware ----------------------------------------------------------------

export const firmwareFileSchema = z.object({
  name: z.string(),
  size: z.number().int().nullable(),
  oui: z.string().nullable(),
  productClass: z.string().nullable(),
  version: z.string().nullable(),
  uploadedAt: z.number().int().nullable(),
});
export type FirmwareFile = z.infer<typeof firmwareFileSchema>;

export const pushFirmwareSchema = z.object({
  fileName: z.string().min(1).max(200),
});
export type PushFirmwareRequest = z.infer<typeof pushFirmwareSchema>;

/**
 * Firmware campaigns: staged rollout with pause-on-failure. Created ≠
 * started — starting is explicit. The next batch goes only when the current
 * one has fully confirmed (device informed with the target version).
 */
export const campaignStatusSchema = z.enum(['draft', 'running', 'paused', 'completed', 'cancelled']);
export type CampaignStatus = z.infer<typeof campaignStatusSchema>;

export const createCampaignSchema = z.object({
  name: z.string().min(1).max(120),
  fileName: z.string().min(1).max(200),
  productClass: z.string().min(1).max(80),
  targetVersion: z.string().min(1).max(80),
  batchSize: z.coerce.number().int().min(1).max(50).default(1),
  pauseOnFailure: z.boolean().default(true),
});
export type CreateCampaignRequest = z.infer<typeof createCampaignSchema>;

export const firmwareCampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  fileName: z.string(),
  productClass: z.string(),
  targetVersion: z.string(),
  batchSize: z.number().int(),
  pauseOnFailure: z.boolean(),
  status: campaignStatusSchema,
  createdAt: z.number().int(),
  startedAt: z.number().int().nullable(),
  finishedAt: z.number().int().nullable(),
  counts: z.object({
    pending: z.number().int(),
    pushed: z.number().int(),
    confirmed: z.number().int(),
    failed: z.number().int(),
    total: z.number().int(),
  }),
});
export type FirmwareCampaign = z.infer<typeof firmwareCampaignSchema>;

/**
 * How a member confirmed. `version` = it informed with the target version.
 * `reboot` = the image leaves the reported version alone (Tenda HG3 stays on
 * "v2.0.0" through a 300003070 build), so the proof is a boot after the push,
 * an inform after that boot, and no GenieACS fault since the push.
 */
export const campaignConfirmedBySchema = z.enum(['version', 'reboot']);
export type CampaignConfirmedBy = z.infer<typeof campaignConfirmedBySchema>;

export const campaignDeviceSchema = z.object({
  deviceId: z.string(),
  serial: z.string().nullable(),
  fromVersion: z.string().nullable(),
  status: z.enum(['pending', 'pushed', 'confirmed', 'failed', 'skipped']),
  pushedAt: z.number().int().nullable(),
  confirmedAt: z.number().int().nullable(),
  /** Null until confirmed. */
  confirmedBy: campaignConfirmedBySchema.nullable(),
  error: z.string().nullable(),
});
export type CampaignDevice = z.infer<typeof campaignDeviceSchema>;

/**
 * Body of `POST /firmware/campaigns/:id/:action`. `retryFailed` is honoured
 * by `start` only: every failed member goes back to pending and is re-pushed
 * as part of a normal batch once the campaign resumes. Off by default — a
 * plain resume keeps its failures as they are.
 */
export const campaignActionSchema = z.object({
  retryFailed: z.boolean().default(false),
});
export type CampaignActionRequest = z.infer<typeof campaignActionSchema>;

// --- System settings ---------------------------------------------------------

/** Effective configuration, sanitised — shown on Settings → Main. */
export const systemInfoSchema = z.object({
  cwmpUrl: z.string(),
  nbiUrl: z.string(),
  fileServerUrl: z.string(),
  genieUiUrl: z.string(),
  pollIntervalMs: z.number().int(),
  onlineWindowSeconds: z.number().int(),
  execAdapter: z.string(),
  wgInterface: z.string(),
  wgEndpoint: z.string(),
  wgPool: z.string(),
  ovpnServer: z.string(),
  ovpnSubnet: z.string(),
  acs: z.object({
    ok: z.boolean(),
    latencyMs: z.number().int().nullable(),
    error: z.string().nullable(),
  }),
  /**
   * Where this install's MCP server actually is, so the setup card can stop
   * printing `/path/to/simpleacs/...` and print the truth.
   *
   * `built` is the half that matters. The entry path is derivable whether or
   * not anything is there; what an operator needs to know before pasting a
   * config is whether the file EXISTS — a client pointed at a missing script
   * fails as "MCP server crashed on startup", which reads like a broken
   * platform rather than a build that never ran.
   *
   * `node` is the interpreter this API is itself running under, absolute.
   * Bare `node` in a snippet resolves against the assistant's PATH, which on
   * a machine with nvm or a system node is routinely a different version than
   * the one the platform was installed with.
   */
  mcp: z.object({
    /** Absolute path to the server's entry point on the machine running this API. */
    entry: z.string(),
    /** Whether that file is there right now. False means the build has not run. */
    built: z.boolean(),
    /** Absolute path to the node binary this API runs under. */
    node: z.string(),
  }),
});
export type SystemInfo = z.infer<typeof systemInfoSchema>;

/**
 * A timing the operator may change without a redeploy.
 *
 * `source` is the honest half: the Settings page has always shown what `.env`
 * says, and once a value can be overridden the page has to say which of the
 * two is actually in force.
 */
export const tunableSchema = z.object({
  key: z.string(),
  label: z.string(),
  hint: z.string(),
  unit: z.string(),
  min: z.number().int(),
  max: z.number().int(),
  value: z.number().int(),
  source: z.enum(['operator', 'environment']),
});
export type Tunable = z.infer<typeof tunableSchema>;

/** `null` puts the setting back to whatever `.env` says. */
export const setTunableSchema = z.object({
  value: z.number().int().nullable(),
});
export type SetTunableInput = z.infer<typeof setTunableSchema>;
